import { api } from '@scoutus/sports-metrics';
import type { GetAllCoachesForAdminParams } from '@scoutus/sports-metrics';
import BrandRecruiterRequestsService from '../service/BrandRecruiterRequestsService';
import { dedupeAsync } from './dedupeAsync';

/** Keep header / overview pulses from re-hitting slow count endpoints. */
const PENDING_PULSE_TTL_MS = 45_000;

/**
 * Pulse only needs `data.count`. Use limit:1 so the list payload stays tiny.
 * (API also supports count_only — wire it once @scoutus/sports-metrics is regenerated.)
 */
async function fetchCoachOnboardingCount(
  onboardingStatus: 'pending' | 'stalled',
): Promise<number> {
  const params: GetAllCoachesForAdminParams = {
    page: 1,
    limit: 1,
    onboarding_status: onboardingStatus,
  };
  const response = await api.admin.getAllCoachesForAdmin(params);
  return response?.data?.count ?? 0;
}

function scoutCountFromResponse(response: unknown): number {
  if (Array.isArray(response)) {
    return response.length;
  }
  if (!response || typeof response !== 'object') {
    return 0;
  }
  const record = response as {
    count?: unknown;
    data?: unknown;
  };
  if (typeof record.count === 'number') {
    return record.count;
  }
  if (typeof record.count === 'string' && record.count.trim() !== '') {
    const parsed = Number(record.count);
    if (!Number.isNaN(parsed)) return parsed;
  }
  if (Array.isArray(record.data)) {
    return record.data.length;
  }
  return 0;
}

/** Shared pending-coach pulse used by header notifications and trust overview. */
export async function fetchPendingCoachCount(): Promise<number> {
  return dedupeAsync(
    'coaches:pending-count',
    () => fetchCoachOnboardingCount('pending'),
    { ttlMs: PENDING_PULSE_TTL_MS },
  );
}

export async function fetchStalledCoachCount(): Promise<number> {
  return dedupeAsync(
    'coaches:stalled-count',
    () => fetchCoachOnboardingCount('stalled'),
    { ttlMs: PENDING_PULSE_TTL_MS },
  );
}

export async function fetchPendingBrandRecruiterCount(): Promise<number> {
  return dedupeAsync(
    'brand:pending-count',
    async () => {
      const response = await BrandRecruiterRequestsService.getBrandRecruiterRequests({
        status: 'pending',
        page: 1,
        limit: 1,
      });
      return response.count ?? 0;
    },
    { ttlMs: PENDING_PULSE_TTL_MS },
  );
}

export async function fetchPendingScoutCount(): Promise<number> {
  return dedupeAsync(
    'scouts:pending-count',
    async () => {
      const response = await api.admin.getScoutRequests({ status: 'pending' });
      return scoutCountFromResponse(response);
    },
    { ttlMs: PENDING_PULSE_TTL_MS },
  );
}
